import React, { useCallback, useState } from "react";
import { useTranslation } from "react-i18next";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import PauseIcon from "@mui/icons-material/Pause";
import StopIcon from "@mui/icons-material/Stop";
import SkipNextIcon from "@mui/icons-material/SkipNext";
import FastForwardIcon from "@mui/icons-material/FastForward";
import CancelOutlinedIcon from "@mui/icons-material/CancelOutlined";
import NavigateNextIcon from "@mui/icons-material/NavigateNext";
import NavigateBeforeIcon from "@mui/icons-material/NavigateBefore";
import ReplayIcon from "@mui/icons-material/Replay";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import { useCoachExerciseLiveActions } from "../../coaching/coachExerciseLiveActions";
import { confirmFinalizeExercise } from "../../coaching/confirmFinalizeExercise";
import {
  confirmSkipExercise,
  confirmSkipSequence,
} from "../../coaching/confirmSkipTraining";
import { getKotStartRoundButtonLabel } from "../../components/KotCoachStartRoundBanner";
import {
  TE_STATE_PAUSED,
  TE_STATE_STARTED,
  TE_STATE_FINALIZED,
  TE_STATE_CANCELLED,
  resolveExerciseStateIdFromRow,
} from "../../utils/coachExerciseNavigation";
import { isKotExercise, isTeSequences } from "../../utils/teExecutionType";
import { resolveCoachNextSequenceInfo } from "../../utils/liveTrainingSequenceAutoeval";

function confirmFinalizeSession(t) {
  const translate = typeof t === "function" ? t : null;
  const message =
    (translate
      ? translate(
          "confirmFinalizeSession",
          "Finalize this training session? Players will see session results."
        )
      : null) ||
    "Finalize this training session? Players will see session results.";
  return typeof window.confirm === "function" ? window.confirm(message) : false;
}

function formatActionError(code, t) {
  if (code === "coach_required") {
    return t("liveTrainingCoachRequired") || "Coach session access required.";
  }
  if (code === "sequence_players_missing") {
    return (
      t("liveTrainingSequencePlayersMissing") ||
      "Assign at least one player to each sequence before starting."
    );
  }
  if (code === "socket_disconnected") {
    return (
      t("liveTrainingStartConnectionError") ||
      "Lost connection to the session. Refresh and try Start again."
    );
  }
  if (code === "start_timeout") {
    return (
      t("liveTrainingStartUnconfirmed") ||
      "Start did not confirm. If the timer is not running, try again."
    );
  }
  if (code === "ack_timeout") {
    return (
      t("liveTrainingActionUnconfirmed") ||
      "The server did not confirm. Check the timer and try again."
    );
  }
  if (code === "not_terminal") {
    return (
      t("liveTrainingRestartNotFinished") ||
      "Exercise is still in progress."
    );
  }
  return code || t("error") || "Error";
}

function isRestartSuccess(ack) {
  if (!ack) return false;
  if (ack.ok === false || ack.status === "error") return false;
  return ack.ok === true || ack.status === "success";
}

function isTerminalExerciseState(stateId) {
  return stateId === TE_STATE_FINALIZED || stateId === TE_STATE_CANCELLED;
}

export function LiveTrainingCoachBar({
  socket,
  tsId,
  exercise,
  exerciseStateId,
  awaitingManualSequenceStart,
  awaitingAutoSequenceStart = false,
  sequenceTransitionPauseRemaining = null,
  requestRefresh,
  onSelectExercise,
  previousExercise = null,
  nextExercise = null,
  hasNext,
  hasPrev = false,
  realtimeByTeId = {},
  actionError,
  onActionError,
  kotRound = null,
  onCoachStopRound = null,
  onFinalizeSession = null,
  finalizingSession = false,
}) {
  const { t, i18n } = useTranslation();
  const teId = exercise?.te_id;
  const [starting, setStarting] = useState(false);
  const [finalizing, setFinalizing] = useState(false);
  const [startingNextSeq, setStartingNextSeq] = useState(false);
  const [skippingSequence, setSkippingSequence] = useState(false);
  const [relaunching, setRelaunching] = useState(false);
  const actions = useCoachExerciseLiveActions({
    socket,
    tsId,
    requestRefresh,
    coachRole: true,
    teId,
  });

  const startTargetExercise = useCallback(
    (targetTeId) =>
      new Promise((resolve) => {
        actions.startExercise(targetTeId, (ack) => resolve(ack));
      }),
    [actions]
  );

  const relaunchExercise = useCallback(
    async (targetExercise, { selectFirst = false } = {}) => {
      if (!targetExercise?.te_id || relaunching || starting || finalizing) {
        return;
      }
      setRelaunching(true);
      onActionError?.(null);
      try {
        if (selectFirst && onSelectExercise) {
          onSelectExercise(targetExercise);
        }
        const sid = resolveExerciseStateIdFromRow(
          targetExercise,
          realtimeByTeId
        );
        if (isTerminalExerciseState(sid)) {
          const restartAck = await actions.restartExercise(targetExercise.te_id);
          if (!isRestartSuccess(restartAck)) {
            onActionError?.(
              formatActionError(
                restartAck?.code || restartAck?.message || "restart_failed",
                t
              )
            );
            return;
          }
        }
        const startAck = await startTargetExercise(targetExercise.te_id);
        if (startAck?.ok === false) {
          onActionError?.(
            formatActionError(startAck.code || startAck.error_code, t)
          );
        }
      } finally {
        setRelaunching(false);
      }
    },
    [
      relaunching,
      starting,
      finalizing,
      onSelectExercise,
      realtimeByTeId,
      actions,
      startTargetExercise,
      onActionError,
      t,
    ]
  );

  const handleStart = useCallback(() => {
    if (!teId || starting) return;
    setStarting(true);
    onActionError?.(null);
    actions.startExercise(teId, (ack) => {
      setStarting(false);
      if (ack?.ok === false) {
        onActionError?.(formatActionError(ack.code || ack.error_code, t));
      }
    });
  }, [teId, starting, actions, onActionError, t]);

  const handleRestart = useCallback(() => {
    if (!exercise) return;
    void relaunchExercise(exercise);
  }, [exercise, relaunchExercise]);

  const handlePrevious = useCallback(() => {
    if (!previousExercise) return;
    void relaunchExercise(previousExercise, { selectFirst: true });
  }, [previousExercise, relaunchExercise]);

  const handleNext = useCallback(() => {
    if (!nextExercise) return;
    void relaunchExercise(nextExercise, { selectFirst: true });
  }, [nextExercise, relaunchExercise]);

  const handleStartNextSequence = useCallback(() => {
    if (!teId || startingNextSeq || finalizing || skippingSequence) return;
    setStartingNextSeq(true);
    onActionError?.(null);
    actions.startNextSequence(teId, (ack) => {
      setStartingNextSeq(false);
      if (ack?.ok === false) {
        onActionError?.(formatActionError(ack.code || ack.error_code, t));
      }
    });
  }, [teId, startingNextSeq, finalizing, skippingSequence, actions, onActionError, t]);

  const handleSkipSequence = useCallback(() => {
    if (!teId || skippingSequence || finalizing || startingNextSeq) return;
    const confirmed = confirmSkipSequence({
      sequences: exercise?.sequences,
      currentSequence: exercise?.currentSequence,
      t,
    });
    if (!confirmed) return;
    setSkippingSequence(true);
    onActionError?.(null);
    actions.skipSequence(teId, (ack) => {
      setSkippingSequence(false);
      if (ack?.ok === false) {
        onActionError?.(formatActionError(ack.code || ack.error_code, t));
      }
    });
  }, [
    teId,
    skippingSequence,
    finalizing,
    startingNextSeq,
    exercise?.sequences,
    exercise?.currentSequence,
    actions,
    onActionError,
    t,
  ]);

  const handleSkipExercise = useCallback(async () => {
    if (!teId || finalizing || starting) return;
    const confirmed = confirmSkipExercise({ hasNext, t });
    if (!confirmed) return;
    setFinalizing(true);
    onActionError?.(null);
    try {
      await actions.finalizeExercise(teId);
    } finally {
      setFinalizing(false);
    }
  }, [teId, finalizing, starting, hasNext, actions, onActionError, t]);

  const handleFinalize = useCallback(async () => {
    if (!teId || finalizing || startingNextSeq || skippingSequence) return;
    const confirmed = confirmFinalizeExercise({
      sequences: exercise?.sequences,
      currentSequence: exercise?.currentSequence,
      awaitingManualSequenceStart,
      t,
    });
    if (!confirmed) return;
    setFinalizing(true);
    onActionError?.(null);
    try {
      await actions.finalizeExercise(teId);
    } finally {
      setFinalizing(false);
    }
  }, [
    teId,
    finalizing,
    startingNextSeq,
    skippingSequence,
    exercise?.sequences,
    exercise?.currentSequence,
    awaitingManualSequenceStart,
    actions,
    onActionError,
    t,
  ]);

  const handleFinalizeSession = useCallback(async () => {
    if (!onFinalizeSession || finalizingSession || finalizing || relaunching) {
      return;
    }
    if (!confirmFinalizeSession(t)) return;
    onActionError?.(null);
    try {
      await onFinalizeSession();
    } catch (err) {
      onActionError?.(err?.message || t("error") || "Error");
    }
  }, [
    onFinalizeSession,
    finalizingSession,
    finalizing,
    relaunching,
    onActionError,
    t,
  ]);

  if (!teId || !socket) return null;

  const isStarted = exerciseStateId === TE_STATE_STARTED;
  const isPaused = exerciseStateId === TE_STATE_PAUSED;
  const isFinalized = exerciseStateId === TE_STATE_FINALIZED;
  const isCancelled = exerciseStateId === TE_STATE_CANCELLED;
  const isTerminal = isFinalized || isCancelled;
  const isRunning = isStarted || isPaused;
  const isScheduled = !isStarted && !isPaused && !isFinalized && !isCancelled;
  const isKot = exercise ? isKotExercise(exercise) : false;
  const actionsBusy =
    finalizing ||
    startingNextSeq ||
    skippingSequence ||
    relaunching ||
    finalizingSession;

  const showSkipSequence =
    isTeSequences(exercise) && isRunning && !isKot;

  const showSkipExercise = isScheduled;

  const showNextSeq =
    awaitingManualSequenceStart &&
    !awaitingAutoSequenceStart &&
    isTeSequences(exercise) &&
    isRunning &&
    !isKot;

  const showAutoPause =
    awaitingAutoSequenceStart &&
    isTeSequences(exercise) &&
    isRunning &&
    !isKot;

  const formatPauseClock = (sec) => {
    const s = Math.max(0, Math.floor(Number(sec) || 0));
    const m = Math.floor(s / 60);
    const r = s % 60;
    return `${m}:${r.toString().padStart(2, "0")}`;
  };

  const kotStartLabel = getKotStartRoundButtonLabel(t, {
    starting: kotRound?.startingRound,
    preparingNextRound: kotRound?.preparingNextRound,
    roundNumber: kotRound?.roundNumber ?? null,
  });

  const lang = (i18n.language || "en").split("-")[0];
  const sequenceInProgress =
    isTeSequences(exercise) && isRunning && !showNextSeq && !showAutoPause;
  const nextSeq =
    !isKot && (exercise?.sequences?.length || 0) > 0
      ? resolveCoachNextSequenceInfo(exercise, {
          lang,
          sequenceInProgress,
          awaitingManualSequenceStart,
          awaitingAutoSequenceStart,
        })
      : null;
  const showSeqNav = Boolean(nextSeq);

  return (
    <div className="lt-coach-bar-wrap" data-tour-id="live-coach-start">
      {actionError ? (
        <p className="lt-coach-bar__error" role="alert">
          {actionError}
        </p>
      ) : null}
      <div className={`lt-coach-bar-chrome${showSeqNav ? " lt-coach-bar-chrome--with-seq" : ""}`}>
        {showSeqNav ? (
          <div className="lt-coach-bar__seq-nav" aria-live="polite">
            <p className="lt-coach-bar__seq-row lt-coach-bar__seq-row--next">
              <span className="lt-coach-bar__seq-kicker">
                {t("liveTrainingNextSeq", "Next Seq")}
              </span>
              <span className="lt-coach-bar__seq-value">{nextSeq.caption}</span>
            </p>
          </div>
        ) : null}
        <div className="lt-coach-bar" role="toolbar" aria-label={t("coachControls")}>
        {showAutoPause ? (
          <div
            className="lt-coach-bar__btn lt-coach-bar__btn--auto-pause"
            role="status"
            aria-live="polite"
          >
            {t("sequenceTransitionPause") || "Next sequence in"}{" "}
            {formatPauseClock(sequenceTransitionPauseRemaining ?? 0)}
          </div>
        ) : null}
        {showNextSeq ? (
          <button
            type="button"
            className="lt-coach-bar__btn lt-coach-bar__btn--primary lt-coach-bar__btn--next-seq"
            onClick={handleStartNextSequence}
            disabled={actionsBusy}
          >
            <SkipNextIcon /> {t("liveTrainingNextSequence")}
          </button>
        ) : null}

        {!isStarted && !isPaused && !isFinalized && !isCancelled ? (
          <button
            type="button"
            className="lt-coach-bar__btn lt-coach-bar__btn--primary"
            onClick={handleStart}
            disabled={starting || finalizing || relaunching}
          >
            <PlayArrowIcon /> {starting ? t("loading") || "…" : t("start")}
          </button>
        ) : null}
        {showSkipExercise ? (
          <button
            type="button"
            className="lt-coach-bar__btn lt-coach-bar__btn--skip"
            onClick={handleSkipExercise}
            disabled={starting || finalizing || relaunching}
          >
            <CancelOutlinedIcon /> {t("liveTrainingSkipExercise") || "Skip exercise"}
          </button>
        ) : null}
        {isStarted && !showNextSeq && !isKot ? (
          <button
            type="button"
            className="lt-coach-bar__btn"
            onClick={() => actions.pauseExercise(teId)}
            disabled={actionsBusy}
          >
            <PauseIcon /> {t("pause")}
          </button>
        ) : null}
        {isPaused && !showNextSeq && !isKot ? (
          <button
            type="button"
            className="lt-coach-bar__btn lt-coach-bar__btn--primary"
            onClick={() => actions.resumeExercise(teId, exercise?.timer)}
            disabled={actionsBusy}
          >
            <PlayArrowIcon /> {t("resume")}
          </button>
        ) : null}
        {isKot && isStarted && !showNextSeq && kotRound?.awaitingCoachStart ? (
          <button
            type="button"
            className="lt-coach-bar__btn lt-coach-bar__btn--primary"
            disabled={!kotRound.canStartRound || kotRound.startingRound}
            onClick={() => kotRound.startNextRound?.()}
            title={
              !kotRound.canStartRound
                ? kotRound.preparingNextRound
                  ? t("kotPreparingNextRound")
                  : t("kotStartRoundWhenReady")
                : undefined
            }
          >
            <PlayArrowIcon /> {kotStartLabel}
          </button>
        ) : null}
        {isKot && isRunning && kotRound?.canRequestStopRound && onCoachStopRound ? (
          <button
            type="button"
            className="lt-coach-bar__btn lt-coach-bar__btn--kot-stop"
            disabled={kotRound.stoppingRound || actionsBusy}
            onClick={() => onCoachStopRound()}
            aria-label={t("kotCoachStopRound")}
          >
            <StopIcon />
            {kotRound.stoppingRound
              ? t("kotCoachStoppingRound")
              : t("kotCoachStopRound")}
          </button>
        ) : null}
        {showSkipSequence ? (
          <button
            type="button"
            className="lt-coach-bar__btn lt-coach-bar__btn--skip"
            onClick={handleSkipSequence}
            disabled={actionsBusy}
          >
            <FastForwardIcon /> {t("liveTrainingSkipSequence") || "Skip sequence"}
          </button>
        ) : null}
        {isRunning ? (
          <button
            type="button"
            className="lt-coach-bar__btn lt-coach-bar__btn--danger"
            onClick={handleFinalize}
            disabled={actionsBusy}
          >
            <StopIcon /> {t("finalize")}
          </button>
        ) : null}
        {isTerminal ? (
          <>
            {hasPrev && previousExercise ? (
              <button
                type="button"
                className="lt-coach-bar__btn"
                onClick={handlePrevious}
                disabled={actionsBusy}
              >
                <NavigateBeforeIcon />{" "}
                {relaunching
                  ? t("loading") || "…"
                  : t("liveTrainingPreviousExercise") || t("previous")}
              </button>
            ) : null}
            <button
              type="button"
              className="lt-coach-bar__btn lt-coach-bar__btn--primary"
              onClick={handleRestart}
              disabled={actionsBusy}
            >
              <ReplayIcon />{" "}
              {relaunching
                ? t("loading") || "…"
                : t("liveTrainingRestartExercise") || "Restart"}
            </button>
            {hasNext && nextExercise ? (
              <button
                type="button"
                className="lt-coach-bar__btn lt-coach-bar__btn--primary"
                onClick={handleNext}
                disabled={actionsBusy}
              >
                <NavigateNextIcon />{" "}
                {relaunching
                  ? t("loading") || "…"
                  : t("liveTrainingStartNextExercise") || t("next")}
              </button>
            ) : onFinalizeSession ? (
              <button
                type="button"
                className="lt-coach-bar__btn lt-coach-bar__btn--danger"
                onClick={handleFinalizeSession}
                disabled={actionsBusy}
              >
                <CheckCircleOutlineIcon />{" "}
                {finalizingSession
                  ? t("loading") || "…"
                  : t("liveTrainingFinalizeSession") || "Finalize session"}
              </button>
            ) : null}
          </>
        ) : null}
        </div>
      </div>
    </div>
  );
}

export default LiveTrainingCoachBar;
